import { useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import AuditHeader, { AuditMoreMenu } from "@/components/audit/AuditHeader";
import PageHeader from "@/components/shell/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";
import { chooseAuditAccount } from "@/lib/audit-account";
import { auditSearchSchema } from "@/lib/search-params";
import { trpc } from "@/lib/trpc";
import { workspaceModuleRedirect } from "@/lib/workspace-module-access";

// Audit (audit guide ticket 02): one account at a time, the same page as the
// desktop app without its Agent tab. The header names the account, with a
// picker only when the Company has several; the tabs are Reports,
// Proposals and Actions. Viewers get no Audit route; the server rejects them
// too. The Report page lives outside this layout (audit_.reports).
export const Route = createFileRoute("/_authenticated/$orgSlug/audit")({
  validateSearch: auditSearchSchema,
  beforeLoad: async ({ params, context }) => {
    const viewer = await context.queryClient.ensureQueryData(
      trpc.organization.viewer.queryOptions(),
    );
    const target = workspaceModuleRedirect(viewer, params.orgSlug);
    if (target) throw redirect(target);
  },
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(
      trpc.audit.accounts.queryOptions({ orgSlug: params.orgSlug }),
    ),
  component: AuditLayout,
});

// The account the page shows: the link's, else the one last chosen here,
// else the first.
export function useAuditAccount() {
  const { orgSlug } = Route.useParams();
  const { account } = Route.useSearch();
  const { data: accounts } = useSuspenseQuery(trpc.audit.accounts.queryOptions({ orgSlug }));
  return { accounts, account: chooseAuditAccount(orgSlug, accounts, account) };
}

// The tabs render only under an account; without one the layout says why.
export function useAuditAccountId() {
  const { account } = useAuditAccount();
  if (!account) throw new Error("The Audit tabs render only with an account");
  return account.accountId;
}

function AuditLayout() {
  const { orgSlug } = Route.useParams();
  const { accounts, account } = useAuditAccount();
  if (!account) {
    return (
      <Page>
        <PageHeader title="Audit" actions={<AuditMoreMenu />} />
        <PageBody>
          <EmptyState title="No Google account is assigned to this Company.">
            A super admin or Company manager assigns one in Settings › Companies.
          </EmptyState>
        </PageBody>
      </Page>
    );
  }
  // Each tab fills the body; the header and its tabs stay put between them.
  return (
    <Page>
      <AuditHeader orgSlug={orgSlug} accounts={accounts} account={account} />
      <PageBody>
        <Outlet />
      </PageBody>
    </Page>
  );
}
